import {
  FC,
  Fragment,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Box, Flex, useBreakpoints } from "@otrium/core";
import { useRouter } from "next/router";
import { connectInfiniteHits } from "react-instantsearch-dom";
import { Trans } from "@lingui/macro";
import {
  connectStateResults,
  InfiniteHitsProvided,
  SearchState,
  StateResultsProvided,
} from "react-instantsearch-core";
import { SearchClient } from "algoliasearch/dist/algoliasearch-lite";
import { Button } from "src/atoms/Button";
import {
  ConfigureProps,
  DeprecatedAlgoliaSearchProduct,
} from "src/types/Algolia";
import { SeperatorMessage } from "src/molecules/SeperatorMessage";
import { arrayToArrays } from "src/utils/arrayToArrays";
import { ProductsGrid } from "src/atoms/ProductsGrid";
import { CustomHit } from "./CustomHit";
import { DesktopFilter } from "./DesktopFilterVeiw/DesktopFilter";
import { SortByFilter } from "./DesktopFilterVeiw/SortByFilter";
import { StickyDesktopFilter } from "./DesktopFilterVeiw/StickyDesktopFilter";
import { VirtualInstantSearch } from "./VirtualInstantSearch";
import { NoAlgoliaProductsFound } from "./NoAlgoliaProductsFound";
import {
  LimitType,
  useSegmentProductList,
} from "src/hooks/useSegmentProductList";
import { PLP_PAGE_TYPE, useSegment } from "src/hooks/useSegment";
import { SEGMENT_LIST_FORMAT, SEGMENT_LIST_TYPE } from "src/segment";
import { localeFix } from "src/utils/genderLocale/genderLocale";
import useFilterProductLabels from "src/hooks/useFilterProductLabels";
import { PLPType } from "src/types/PLPType.d";
import { PageContentType } from "src/types/graphql.d";
import contextKeys from "src/utils/algolia/contextKeys";
import { RankingInfo } from "src/contexts/app.context";
import { ProductRedirect } from "../ProductCardWishlist/ProductCardWishlist";
import { GATED_POPUP_REDIRECT_DATA } from "src/constants/gatedItem";
import {
  removeStorageItem,
  setStorageJSON,
} from "src/utils/helper/localStorage";
import { useSearchParams } from "next/navigation";
import { StyledAuthBanner } from "../ProductList/ProductList.styled";
import { AuthModule } from "../AuthModule";
import { useNewIsLoggedIn } from "src/hooks/useIsLoggedIn";
import styled from "@emotion/styled";

interface PageStatsProps {
  nbHits: number;
  page: number;
  nbPages: number;
  index: string;
  queryID: any;
}

const GridContainer = styled(Flex)`
  padding: 0 ${({ theme }) => theme.space.space16};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 0;
  }
`;

const PaginationState = connectStateResults<
  StateResultsProvided & { onChange: (props: PageStatsProps) => void }
>(({ allSearchResults, onChange }) => {
  const { nbHits, page, nbPages, index, queryID } = allSearchResults || {};

  useEffect(() => {
    onChange({ nbHits, page, nbPages, index, queryID });
  }, [nbHits, page, nbPages, index, queryID]); // eslint-disable-line react-hooks/exhaustive-deps

  return <></>;
});

const ShowPreviousButton = ({
  hasPrevious,
  refinePrevious,
}: {
  hasPrevious: boolean;
  refinePrevious: () => void;
}) => {
  if (!hasPrevious) {
    return null;
  }
  return (
    <Flex justifyContent="center" alignItems="center" mb={4}>
      <Button
        variant="secondary"
        onClick={refinePrevious}
        data-testid="pagination-show-previous"
      >
        <Trans>Show previous</Trans>
      </Button>
    </Flex>
  );
};

const ShowMoreButton = ({
  hasMore,
  refineNext,
}: {
  hasMore: boolean;
  refineNext: () => void;
}) => {
  if (!hasMore) {
    return null;
  }
  return (
    <Flex variant="centered" mt={3}>
      <Button
        variant="secondary"
        onClick={refineNext}
        data-testid="pagination-show-next"
      >
        <Trans>Show next products</Trans>
      </Button>
    </Flex>
  );
};
interface Props extends InfiniteHitsProvided {
  hits: DeprecatedAlgoliaSearchProduct[];
  orderBy: string;
  handleSortBy: (value: string) => void;
  onClose: () => void;
  onMobileFiltersResetCB: (cb: () => void) => void;
  searchClient:
    | (SearchClient & {
        keepSelectedIgnoreInActive?: { [p: string]: string[] };
      })
    | null;
  searchState?: SearchState;
  onSearchStateChange?: (state: SearchState) => void;
  indexName: string;
  defaultPriceRange: { min: number; max: number };
  isModalOpened: boolean;
  hideCategoryTree?: boolean;
  excludedFacetsList?: string[];
  setStatePayload?: (
    payload: SearchState & { selectedCatSlug: string[] }
  ) => void;
  genderId: string;
  configureProps: ConfigureProps;
  noSearchResults?: ReactNode;
  plpType?: PLPType;
  rankingInfo?: RankingInfo;
  categoryTitle?: string;
  categorySlug?: string;
}

const InfiniteHits: FC<Props> = ({
  hits,
  hasPrevious,
  refinePrevious,
  hasMore,
  refineNext,
  orderBy,
  handleSortBy,
  onClose,
  searchClient,
  searchState,
  onSearchStateChange,
  indexName,
  defaultPriceRange,
  isModalOpened,
  hideCategoryTree,
  excludedFacetsList,
  setStatePayload,
  genderId,
  configureProps,
  onMobileFiltersResetCB,
  noSearchResults,
  plpType,
  rankingInfo,
  categorySlug,
  categoryTitle,
}) => {
  const [isFilterSticky, setIsFilterSticky] = useState(false);
  const [pageData, setPageData] = useState<PageStatsProps>({
    nbHits: 0,
    page: 0,
    nbPages: 0,
    index: "",
    queryID: "",
  });
  const [startPageIndex, setStartPageIndex] = useState(0);
  const [items, setItems] = useState(hits);
  const [isDropdownOpened, setIsDropdownOpened] = useState(false);
  const [isButtonPressed, setIsButtonPressed] = useState(false);
  const [currentFilterIndex, setCurrentFilterIndex] = useState<number | null>(
    null
  );
  const { isDesktop, isMobile } = useBreakpoints();
  const searchParams = useSearchParams();
  const { isLoggedIn, isSessionLoading } = useNewIsLoggedIn();

  const router = useRouter();

  const total = pageData.nbHits;

  const initialLoad = useRef(true);

  useEffect(() => {
    if (hits.length > 0 && initialLoad.current) {
      initialLoad.current = false;
    }
  }, [hits]);

  const { segmentProductListFilterOpened } = useSegment();
  const shopType = localeFix({ gender: genderId });

  const {
    algoliaListFiltered,
    algoliaListReset,
    segmentProductOnClick,
    setVisibleProducts,
  } = useSegmentProductList({
    algoliaIndex: pageData.index,
    listFormat: SEGMENT_LIST_FORMAT.default,
    listId:
      plpType === PLPType.SHOP_TYPE ? genderId : configureProps.ruleContexts[1],
    listType:
      plpType === PLPType.SHOP_TYPE
        ? contextKeys[plpType]
        : SEGMENT_LIST_TYPE.products,
    queryID: pageData.queryID,
    numberOfItems: pageData.nbHits,
    shopType,
    rankingInfo,
    query: searchParams.get("q") || undefined,
  });

  const setRedirectData = (
    type: string,
    product?: ProductRedirect,
    actionSource?: string,
    position?: number
  ) => {
    removeStorageItem(GATED_POPUP_REDIRECT_DATA);
    setStorageJSON(GATED_POPUP_REDIRECT_DATA, {
      gatedActionType: type,
      product,
      productListMeta: {
        listFormat: SEGMENT_LIST_FORMAT.default,
        listId:
          plpType === PLPType.SHOP_TYPE
            ? genderId
            : configureProps.ruleContexts[1],
        listType:
          plpType === PLPType.SHOP_TYPE
            ? contextKeys[plpType]
            : SEGMENT_LIST_TYPE.products,
        shopType,
      },
      actionSource,
      position,
      queryID: pageData.queryID,
      algoliaIndex: pageData.index,
    });
  };

  const switchFilters = useCallback(
    (index: number, filterName: string) => () => {
      setCurrentFilterIndex((prevIndex) => {
        if (prevIndex === index) return null;
        void segmentProductListFilterOpened(filterName);
        return index;
      });
    },
    [setCurrentFilterIndex]
  );

  const algoliaListFilteredCB = useCallback(() => {
    if (!searchState) return;
    algoliaListFiltered(searchState);
  }, [searchState, algoliaListFiltered]);

  useEffect(() => {
    onMobileFiltersResetCB(algoliaListReset);
  }, [onMobileFiltersResetCB, algoliaListReset]);

  const closeFilters = useCallback(() => {
    setCurrentFilterIndex(null);
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (initialLoad.current) return;
    algoliaListFilteredCB();
  }, [router.query]);

  const handlePageState = useCallback((pageStats: PageStatsProps) => {
    if (pageStats.page !== undefined) {
      setPageData(pageStats);
    }
  }, []);

  const handleRefineCallback =
    (cb: (...args: any[]) => any[]) =>
    (...args: any[]) => {
      if (!isButtonPressed) {
        cb(args);
        setIsButtonPressed(true);
      }
    };

  const pages = ((items.length > 0 &&
    arrayToArrays(items as [], searchState?.hitsPerPage || 72)) ||
    []) as DeprecatedAlgoliaSearchProduct[][];

  useEffect(() => {
    if (isDropdownOpened) {
      closeFilters();
    }
  }, [isFilterSticky]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!pages.length) {
      return;
    }
    if (pages.length === 1 && pageData.page !== undefined && !isButtonPressed) {
      setStartPageIndex(pageData.page + 1);
    } else {
      if (isButtonPressed) {
        setStartPageIndex(
          Math.max(1, Math.min(startPageIndex, pageData.page + 1))
        );
        setIsButtonPressed(false);
      }
    }
  }, [pages.length, pageData.page]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setIsDropdownOpened(currentFilterIndex !== null);
  }, [currentFilterIndex]);

  useEffect(() => {
    if (!isModalOpened && !isDropdownOpened) {
      setItems(hits);
    }
  }, [hits, isModalOpened, isDropdownOpened]);

  const { data: labels } = useFilterProductLabels({
    products: items,
    pageContentType:
      plpType === PLPType.BRAND_PLP
        ? PageContentType.Brand
        : PageContentType.Category,
  });

  const hideDesktopFilter =
    plpType === PLPType.SEARCH_PLP && hits.length === 0 && !isDropdownOpened;

  const insertBannerAfter = isMobile ? 2 : hideCategoryTree ? 4 : 3;

  return (
    <Box mb={5}>
      <PaginationState onChange={handlePageState} />
      {isDesktop && !hideDesktopFilter && (
        <StickyDesktopFilter checkIsFilterSticky={setIsFilterSticky}>
          <VirtualInstantSearch
            searchClient={searchClient}
            indexName={indexName}
            searchState={searchState}
            onSearchStateChange={onSearchStateChange}
            configureProps={configureProps}
          >
            <DesktopFilter
              onClose={closeFilters}
              onResetFilters={algoliaListReset}
              isFilterSticky={isFilterSticky}
              defaultPriceRange={defaultPriceRange}
              excludedFacetsList={excludedFacetsList}
              keepSelectedIgnoreInActive={
                searchState?.keepSelectedIgnoreInActive
              }
              setStatePayload={setStatePayload}
              currentIndex={currentFilterIndex}
              switchFilters={switchFilters}
            />

            <SortByFilter
              orderBy={orderBy}
              handleSortingValue={handleSortBy}
              mb={isFilterSticky ? 0 : 5}
              data-testid="products-sorting"
            />
          </VirtualInstantSearch>
        </StickyDesktopFilter>
      )}
      <ShowPreviousButton
        hasPrevious={hasPrevious && !!hits.length}
        refinePrevious={handleRefineCallback(refinePrevious)}
      />
      {pages.map((pageHits, i) => {
        return (
          <Fragment key={`page-${i}`}>
            {startPageIndex + i > 1 && (
              <SeperatorMessage>
                <Trans>Page</Trans> {startPageIndex + i}
              </SeperatorMessage>
            )}
            <GridContainer>
              <ProductsGrid hideCategoryTree={hideCategoryTree}>
                {pageHits.map((hit, index) => (
                  <>
                    <CustomHit
                      key={hit.objectID}
                      product={hit}
                      labels={labels}
                      index={index}
                      segmentProductOnClick={segmentProductOnClick}
                      setVisibleProducts={(product, position) =>
                        setVisibleProducts(product, position, LimitType.LENGTH)
                      }
                      queryID={pageData.queryID}
                      algoliaIndex={pageData.index}
                      indexName={indexName}
                      trackingData={{
                        shopType,
                        pageName: `${
                          PLP_PAGE_TYPE[plpType as number]
                        }_product_listing_page`,
                      }}
                      setProductRedirect={setRedirectData}
                    />

                    {index + 1 === insertBannerAfter &&
                      !isSessionLoading &&
                      !isLoggedIn && (
                        <StyledAuthBanner hideCategoryTree={hideCategoryTree}>
                          <AuthModule
                            trackingData={{
                              pageType: plpType,
                              categorySlug,
                              pageName: categoryTitle,
                              shopType,
                            }}
                            noMarging
                          />
                        </StyledAuthBanner>
                      )}
                  </>
                ))}
              </ProductsGrid>
            </GridContainer>
          </Fragment>
        );
      })}
      {hits.length === 0 &&
        !isDropdownOpened &&
        (noSearchResults ?? <NoAlgoliaProductsFound />)}
      {items.length > 0 && (
        <SeperatorMessage data-testid="total-products">
          <Trans>
            Seen {items.length} from {total}
          </Trans>
        </SeperatorMessage>
      )}
      <ShowMoreButton
        hasMore={hasMore}
        refineNext={handleRefineCallback(refineNext)}
      />
    </Box>
  );
};

const CustomInfiniteHits = connectInfiniteHits(InfiniteHits);

export { CustomInfiniteHits };
